import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LoaderCircle, ShieldCheck, Scale } from "lucide-react";
import { Logo } from "../components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Block = ({ title, items, tone }) => (
  <div className="mt-3">
    <p className={`font-jbmono text-[10px] font-bold uppercase tracking-widest ${tone === "no" ? "text-[#EF4444]" : tone === "warn" ? "text-[#F97316]" : "text-[#22C55E]"}`}>{title}</p>
    <ul className="mt-2 space-y-1.5">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2 text-xs text-slate-300">
          <span className={tone === "no" ? "text-[#EF4444]" : "text-[#22C55E]"}>{tone === "no" ? "✕" : "✓"}</span> {it}
        </li>
      ))}
    </ul>
  </div>
);

export default function RulesPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Platform Rules & Regulations — FUNZ";
    axios.get(`${API}/governance/rules`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  return (
    <div data-testid="rules-page" className="min-h-screen bg-[#09090B] text-white">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#09090B]/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3.5">
          <Link to="/" data-testid="rules-logo" className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-display text-base font-extrabold tracking-tight">FUN<span className="text-[#06B6D4]">Z</span></span>
          </Link>
          <span className="rounded-full border border-white/10 px-3 py-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-400">Legal · v{data?.version || "1.0"}</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10">
        <p className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-[0.3em] text-[#A78BFA]"><Scale className="h-4 w-4" /> Governance framework</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Platform Rules & Regulations</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400">
          The enterprise-grade governance framework for hotels, restaurants, kitchens, vendors, loans, finance and administrative operations on FUNZ. Every account agrees to these rules.
        </p>

        {!data ? (
          <LoaderCircle className="mt-12 h-6 w-6 animate-spin text-[#06B6D4]" />
        ) : (
          <>
            <div className="mt-10 space-y-4">
              {data.rules.map((r, i) => (
                <section key={r.id} data-testid={`rule-${r.id}`} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl">
                  <p className="font-display text-lg font-bold"><span className="mr-2">{r.icon}</span>{i + 1}. {r.title}</p>
                  {r.allowed && <Block title="Allowed" items={r.allowed} tone="ok" />}
                  {r.points && <Block title="Rules" items={r.points} tone="ok" />}
                  {r.business && <Block title="Business accounts must provide" items={r.business} tone="warn" />}
                  {r.prohibited && <Block title="Prohibited" items={r.prohibited} tone="no" />}
                  {r.consequences && <Block title="Consequences" items={r.consequences} tone="no" />}
                </section>
              ))}
            </div>

            <section className="mt-10 rounded-3xl border border-[#F5B300]/30 bg-gradient-to-br from-[#F5B300]/10 to-transparent p-6 backdrop-blur-xl" data-testid="golden-rules">
              <p className="font-display text-xl font-extrabold">🏆 Golden Rules</p>
              <ol className="mt-4 grid gap-2 sm:grid-cols-2">
                {data.golden_rules.map((g, i) => (
                  <li key={g} className="flex items-start gap-2.5 rounded-2xl border border-white/10 bg-[#09090B]/60 px-4 py-3 text-xs font-bold text-slate-200">
                    <span className="font-display text-[#F5B300]">{i + 1}</span> {g}
                  </li>
                ))}
              </ol>
            </section>
          </>
        )}

        <p className="mt-10 flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
          <ShieldCheck className="h-3.5 w-3.5" /> FUNZ Hospitality Cloud · updated {data?.updated_at || ""}
        </p>
      </main>
    </div>
  );
}
